import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
  input,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

// services
import {
  CurrencyService,
  ICurrency,
} from '@app/core/services/currency.service';
import { ExchangeRateService } from '@app/core/services/exchange-rate/exchange-rate.service';
import { MessageService } from '@openng/optimus-ui/api';
import { DateConstantsService } from '@app/core/services/date/date-constants.service';
import {
  Ag1ExchangeRatePickerService,
  IExchangeRate,
} from './ag1-exchange-rate-picker.service';

// data fetching
import {
  CurrencyConversion,
  CurrencyConversionQueryVariables,
  ExchangeRateTypeEnum,
} from '@app/generated/graphql';
import { Observable, merge } from 'rxjs';
import { debounceTime, tap, switchMap } from 'rxjs/operators';

// other
import { captureException } from '@sentry/angular';
import { BigDecimal } from 'bigdecimal';

// components
import { Popover } from '@openng/optimus-ui/popover';
import { NgTemplateOutlet, DecimalPipe } from '@angular/common';
import { Bind } from '@openng/optimus-ui/bind';
import {
  NgSelectComponent,
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
} from '@ng-select/ng-select';
import { Button } from '@openng/optimus-ui/button';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ag1FinancialInputComponent } from '../inputs/ag1-financial-input/ag1-financial-input.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface IExchangeRateType {
  value: ExchangeRateTypeEnum;
  title: string;
  icon: string;
  color: string;
  subTitle: string;
}

@Component({
  selector: 'ag1-exchange-rate-picker',
  templateUrl: './ag1-exchange-rate-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    Bind,
    Popover,
    FormsModule,
    ReactiveFormsModule,
    NgSelectComponent,
    Button,
    Tooltip,
    AngularSvgIconModule,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    Ag1FinancialInputComponent,
    DecimalPipe,
  ],
})
export class Ag1ExchangeRatePickerComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _messageService = inject(MessageService);
  private currencyService = inject(CurrencyService);
  private exchangeRateService = inject(ExchangeRateService);
  dateConstantsService = inject(DateConstantsService);

  readonly disableFromCurrencyInput = input(false);
  readonly disableToCurrencyInput = input(false);

  readonly type = input<'inline' | 'menu'>('inline');

  /** Parent-owned service that holds this picker's exchange rate state. */
  readonly exchangeRatePickerService =
    input.required<Ag1ExchangeRatePickerService>();

  // state
  isLoading: boolean = false;

  // constants
  exchangeRateTypes: IExchangeRateType[] = [
    {
      value: ExchangeRateTypeEnum.Custom,
      title: 'Custom',
      icon: 'assets/icons/general/edit.svg',
      color: 'text-french-blue-600',
      subTitle: 'Enter your own exchange rate',
    },
    {
      value: ExchangeRateTypeEnum.Spot,
      title: 'Spot rate',
      icon: 'assets/icons/general/calendar.svg',
      color: 'text-french-blue-600',
      subTitle: 'Choose a rate based on a specific date',
    },
    {
      value: ExchangeRateTypeEnum.Fixed,
      title: 'Fixed',
      icon: 'assets/icons/general/handshake.svg',
      color: 'text-french-blue-600',
      subTitle: 'Use a predefined, fixed exchange rate',
    },
  ];

  // form
  exchangeRateform: FormGroup;

  // data
  currencies: ICurrency[] = [];
  state: IExchangeRate | null = null;
  currencyConversion: CurrencyConversion | null = null;

  // formaters
  numberFormat: string = '1.6-6';
  maxDecimalPlaces: number = 6; // 6 is the typical financial standard when dealing with exchange rates

  isActive: boolean = false;

  readonly exchangeRateMenu = viewChild<Popover>('exchangeRatePopover');

  constructor() {}

  ngOnInit(): void {
    this.isLoading = true;
    this.initializeCurrencies();

    this.exchangeRatePickerService()
      .state$.pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap((state: IExchangeRate | null) => {
          this.isLoading = false;
          this.state = state ? state : null;
          this.initializeForm();
          return this.watchForm();
        }),
      )
      .subscribe();
  }

  private initializeCurrencies(): void {
    this.currencies = this.currencyService.getAllCurrencies();
  }

  private initializeForm(): void {
    this.exchangeRateform = new FormGroup({
      fromCurrency: new FormControl(this.state?.fromCurrency || null, [
        Validators.required,
      ]),
      toCurrency: new FormControl(this.state?.toCurrency || null, [
        Validators.required,
      ]),
      exchangeRateType: new FormControl(this.state?.exchangeRateType || null, [
        Validators.required,
      ]),
      exchangeRateDate: new FormControl(
        this.state?.exchangeRateDate
          ? this.formatDateForInput(this.state.exchangeRateDate)
          : null,
        [Validators.required],
      ),
      exchangeRate: new FormControl(this.state?.exchangeRate || null, [
        Validators.required,
      ]),
    });
  }

  private formatDateForInput(date: Date): string {
    // required since html element input type date only accepts YYYY-MM-DD as a valid date
    if (!date) {
      return null;
    }

    if (typeof date === 'string') {
      return date;
    }

    // eg. 2025-04-01T00:00:00.000Z -> 2025-04-01
    return date.toISOString().split('T')[0];
  }

  private watchForm(): Observable<any> {
    return merge(
      this.watchFromCurrency(),
      this.watchToCurrency(),
      this.watchExchangeRateType(),
      this.watchExchangeRateDate(),
      this.watchExchangeRate(),
    );
  }

  private watchFromCurrency(): Observable<ICurrency> {
    return this.fromCurrency.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
      debounceTime(300),
      tap((value: ICurrency) => {
        if (value === this.toCurrency.value) {
          this.exchangeRate.setValue('1');
          return;
        }
        this.exchangeRate.setValue(null);
        if (this.isSpotRate) {
          this.fetchSpotRate();
        } else if (this.isFixedRate) {
          this.fetchFixedRate();
        }
      }),
    );
  }

  private watchToCurrency(): Observable<ICurrency> {
    return this.toCurrency.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
      debounceTime(300),
      tap((value: ICurrency) => {
        if (value === this.fromCurrency.value) {
          this.exchangeRate.setValue('1');
          return;
        }
        this.exchangeRate.setValue(null);

        // when changing currency refetch the currency conversion
        if (this.isSpotRate) {
          this.fetchSpotRate();
          return;
        }

        if (this.isFixedRate) {
          this.fetchFixedRate();
          return;
        }
      }),
    );
  }

  private watchExchangeRateType(): Observable<ExchangeRateTypeEnum> {
    return this.exchangeRateType.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
      debounceTime(300),
      tap((value: ExchangeRateTypeEnum) => {
        this.exchangeRate.setValue(null);
        // default date to today
        this.exchangeRateDate.setValue(this.formatDateForInput(new Date()));

        // reset currency conversion
        this.currencyConversion = null;

        switch (value) {
          case ExchangeRateTypeEnum.Custom:
            break;
          case ExchangeRateTypeEnum.Spot:
            this.fetchSpotRate();
            break;
          case ExchangeRateTypeEnum.Fixed:
            this.fetchFixedRate();
            break;
        }
      }),
    );
  }

  private watchExchangeRateDate(): Observable<Date> {
    return this.exchangeRateDate.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
      debounceTime(300),
      tap((value: Date) => {
        if (!value) return;
        if (this.isSpotRate) {
          this.exchangeRate.setValue(null);
          this.currencyConversion = null;
          this.fetchSpotRate();
        }
      }),
    );
  }

  private watchExchangeRate(): Observable<string> {
    return this.exchangeRate.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
      debounceTime(300),
      tap((value: string) => {
        const formValues: IExchangeRate = {
          fromCurrency: this.fromCurrency.value,
          toCurrency: this.toCurrency.value,
          exchangeRate: value || null,
          exchangeRateType: this.exchangeRateType.value,
          exchangeRateDate: this.exchangeRateDate.value,
          exchangeableId: this.state?.exchangeableId || null,
          exchangeableType: this.state?.exchangeableType || null,
          currencyConversionId: this.currencyConversion?.id || null,
        };
        this.exchangeRatePickerService().updateState(formValues);
      }),
    );
  }

  get isInline(): boolean {
    return this.type() === 'inline';
  }

  get isMenu(): boolean {
    return this.type() === 'menu';
  }

  get exchangeRateType(): AbstractControl<ExchangeRateTypeEnum, any> {
    return this.exchangeRateform?.get('exchangeRateType');
  }

  get exchangeRateformValue(): IExchangeRate {
    return this.exchangeRateform?.value;
  }

  get fromCurrency(): AbstractControl<ICurrency, any> {
    return this.exchangeRateform?.get('fromCurrency');
  }

  get toCurrency(): AbstractControl<ICurrency, any> {
    return this.exchangeRateform?.get('toCurrency');
  }

  get exchangeRate(): AbstractControl<string, any> {
    return this.exchangeRateform?.get('exchangeRate');
  }

  get exchangeRateDate(): AbstractControl<Date, any> {
    return this.exchangeRateform?.get('exchangeRateDate');
  }

  get isSpotRate(): boolean {
    return this.exchangeRateType.value === ExchangeRateTypeEnum.Spot;
  }

  get isCustomRate(): boolean {
    return this.exchangeRateType.value === ExchangeRateTypeEnum.Custom;
  }

  get isFixedRate(): boolean {
    return this.exchangeRateType.value === ExchangeRateTypeEnum.Fixed;
  }

  swapCurrencies(): void {
    if (this.swapDisabled) {
      return;
    }

    const tempCurrency: ICurrency = this.fromCurrency.value;
    this.fromCurrency.setValue(this.toCurrency.value);
    this.toCurrency.setValue(tempCurrency);
  }

  resetState(): void {
    this.currencyConversion = null;

    // reset everything except currencies
    this.exchangeRateType.setValue(null, { emitEvent: false });
    this.exchangeRateDate.setValue(null, { emitEvent: false });
    this.exchangeRate.reset();
    this.exchangeRate.setValue(null, { emitEvent: false });

    this.state = this.exchangeRateformValue;

    this.isLoading = false;
    this.exchangeRatePickerService().updateState(this.state);
  }

  errorState(): void {
    this.currencyConversion = null; // maybe
    this.isLoading = false;
    this.enableFormControls();
  }

  loadingState(): void {
    this.isLoading = true;
    this.disableFormControls();
  }

  disableFormControls(): void {
    Object.keys(this.exchangeRateform.controls).forEach((key: string) => {
      const control: AbstractControl<any, any> = this.exchangeRateform.get(key);
      if (control) {
        control.disable({ emitEvent: false }); // Prevent triggering valueChanges
      }
    });
  }

  enableFormControls(): void {
    Object.keys(this.exchangeRateform.controls).forEach((key: string) => {
      const control: AbstractControl<any, any> = this.exchangeRateform.get(key);
      if (control) {
        control.enable({ emitEvent: false }); // Prevent triggering valueChanges
      }
    });
  }

  completedState(): void {
    this.isLoading = false;
    this.enableFormControls();
  }

  private fetchSpotRate(): void {
    if (this.currencyConversion) {
      this.setExchangeRate(this.currencyConversion);
      return;
    }

    // reset current exchange rate
    this.exchangeRate.setValue(null);
    this.currencyConversion = null;

    if (!this.exchangeRateDate.value) {
      return;
    }

    if (!this.fromCurrency.value || !this.toCurrency.value) {
      return;
    }

    this.loadingState();

    const variables: CurrencyConversionQueryVariables = {
      currencyConversionAttributes: {
        isSpotRate: this.isSpotRate,
        date: this.exchangeRateDate.value as unknown as string,
      },
    };

    this.exchangeRateService
      .getExchangeRate(variables)
      .pipe(takeUntilDestroyed(this._destroyRef), debounceTime(300))
      .subscribe(
        (currencyConversion: CurrencyConversion) => {
          this.currencyConversion = currencyConversion;

          if (!currencyConversion) {
            this.errorState();
            return;
          }

          this.setExchangeRate(currencyConversion);
          this.completedState();
        },
        (err) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Error fetching spot rate',
          });
          this.resetState();
          captureException(err);
        },
      );
  }

  private fetchFixedRate(): void {
    if (this.currencyConversion) {
      this.setExchangeRate(this.currencyConversion);
      return;
    }

    this.exchangeRate.setValue(null);
    this.currencyConversion = null;

    if (!this.exchangeRateDate.value) {
      return;
    }

    if (!this.fromCurrency.value || !this.toCurrency.value) {
      return;
    }

    this.loadingState();

    const variables: CurrencyConversionQueryVariables = {
      currencyConversionAttributes: {
        isFixedRate: this.isFixedRate,
        date: this.exchangeRateDate.value as unknown as string,
      },
    };

    this.exchangeRateService
      .getExchangeRate(variables)
      .pipe(takeUntilDestroyed(this._destroyRef), debounceTime(300))
      .subscribe(
        (currencyConversion: CurrencyConversion) => {
          this.currencyConversion = currencyConversion;
          if (!currencyConversion) {
            this.errorState();
            return;
          }

          this.setExchangeRate(currencyConversion);
          this.completedState();
        },
        (err) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Error fetching fixed rate',
          });
          this.resetState();
          captureException(err);
        },
      );
  }

  private setExchangeRate(currencyConversion: CurrencyConversion): void {
    if (!currencyConversion) {
      return;
    }

    const exchangeRate: BigDecimal | null =
      this.exchangeRateService.getCurrencyConversion({
        fromCurrency: this.fromCurrency.value.currencyCode,
        toCurrency: this.toCurrency.value.currencyCode,
        currencyConversion: currencyConversion,
      });

    if (exchangeRate) {
      this.exchangeRate.setValue(exchangeRate.toString());
    }
  }

  get swapDisabled(): boolean {
    return this.disableFromCurrencyInput() || this.disableToCurrencyInput();
  }

  get errorMessage(): string {
    if (this.isCustomRate) {
      return null;
    }

    if (this.exchangeRate.value) {
      return null;
    }

    if (this.isLoading) {
      return null;
    }

    return `No exchange rate found for ${this.fromCurrency.value.currencyCode}/${this.toCurrency.value.currencyCode}`;
  }

  onMenuClose(): void {
    this.isActive = false;
  }

  onMenuOpen(): void {
    this.isActive = true;
  }

  closeMenu(): void {
    this.exchangeRateMenu()?.hide();
  }

  onConfirm(): void {
    this.closeMenu();
  }

  onClear(): void {
    this.closeMenu();

    this.resetState();
  }
}
